/**
 * @author Michael Hasenstein <hasenstein@yahoo.com>
 * @copyright REFINIO GmbH 2018
 * @license CC-BY-NC-SA-2.5; portions MIT License
 * @version 0.0.1
 */
import { createError } from './errors.js';
import { createInstance } from './instance-creator.js';
import { updateInstance } from './instance-updater.js';
import { lockKeyChain, unlockOrCreateKeyChain } from './keychain/keychain.js';
import { createMessageBus } from './message-bus.js';
import { addCoreRecipesToRuntime, addRecipeToRuntime, clearRuntimeRecipes, hasRecipe } from './object-recipes.js';
import { addEnabledRvMapType, addEnabledRvMapTypeForIdObjects, clearRvMapTypes } from './reverse-map-updater.js';
import { getObjectWithType } from './storage-unversioned-objects.js';
import { getObjectByIdHash } from './storage-versioned-objects.js';
import { createCryptoHash } from './system/crypto-helpers.js';
import { closeStorage, deleteStorage, doesStorageExist, initStorage } from './system/storage-base.js';
import { ID_OBJECT_ATTR } from './util/object.js';
import { isString } from './util/type-checks-basic.js';
const MessageBus = createMessageBus('instance');
let InstanceDirectory;
let InstanceName;
let InstaceOwnerEmail;
let InstanceIdHash;
let InstanceOwnerIdHash;
/**
 * @static
 * @returns {string|undefined} Returns instance directory
 */
export function getInstanceDirectory() {
    return InstanceDirectory;
}
/**
 * @static
 * @returns {string|undefined} Returns the `name`property of the {@link Instance} object of the
 * currently active instance, or `undefined` if the instance has not been initialized yet
 */
export function getInstanceName() {
    return InstanceName;
}
/**
 * @static
 * @returns {SHA256IdHash<Instance>|undefined} Returns the ID hash of the instance
 */
export function getInstanceIdHash() {
    return InstanceIdHash;
}
/**
 * @static
 * @returns {undefined | SHA256IdHash<Person>} Returns the ID hash of the instance owner person
 * object
 */
export function getInstanceOwnerIdHash() {
    return InstanceOwnerIdHash;
}
/**
 * @static
 * @returns {string|undefined} Returns the instance owner email
 */
export function getInstanceOwnerEmail() {
    return InstaceOwnerEmail;
}
/**
 * Load the map files used by the app. If bootstrapping fails because the bootstrap objects
 * don't exist they are silently created.
 * @static
 * @async
 * @param {InstanceOptions} options - A mandatory object with options properties
 * @returns {Promise<Instance>} Returns the Instance object
 */
export async function initInstance({ name, email, secret, ownerName, personEncryptionKeyPair, personSignKeyPair, instanceEncryptionKeyPair, instanceSignKeyPair, wipeStorage = false, encryptStorage = false, directory, nHashCharsForSubDirs = 0, storageInitTimeout = 1000, initialRecipes = [], initiallyEnabledReverseMapTypes, initiallyEnabledReverseMapTypesForIdObjects }) {
    // Our architecture is set up so that one Javascript runtime environment can run one
    // instance. It is not possible to have two different ONE instances in the same Javascript
    // environment. Each instance has its own storage space identified by the Instance ID hash.
    if (InstanceName !== undefined) {
        throw createError('IN-INIT1', { iName: InstanceName });
    }
    if (encryptStorage && !isString(secret)) {
        throw createError('IN-INIT2', { iName: name });
    }
    MessageBus.send('log', `Initializing instance "${name}"`);
    // Catch-22: We need the Instance ID hash to initialize storage - but we cannot call the
    // Instance object creating instance-updater that would return that hash until after storage
    // has been initialized. To escape this contradiction we have to do the hash calculation
    // ourselves, even though the instance-updater will do the same later.
    // Trying to micro-optimize and to avoid doing the same work twice is not worth it though,
    // especially given that instance-updater doesn't do the calculation itself, but it happens
    // implicitly further down the call stack when it tries to load an already existing owner and
    // instance objects.
    const instanceIdHash = await calculateInstanceIdHash(name, email);
    await initStorage({
        instanceIdHash,
        wipeStorage,
        name: directory,
        nHashCharsForSubDirs,
        storageInitTimeout,
        encryptStorage,
        secretForStorageKey: secret
    });
    let instance;
    // Add the CORE-Recipes. Needs to happen before any function using them is called.
    addCoreRecipesToRuntime();
    await unlockOrCreateKeyChain(secret);
    try {
        instance = await getObjectByIdHash(instanceIdHash);
    }
    catch (err) {
        if (err.name !== 'FileNotFoundError') {
            throw err;
        }
        instance = await createInstance({
            name,
            email,
            ownerName,
            personEncryptionKeyPair,
            personSignKeyPair,
            instanceEncryptionKeyPair,
            instanceSignKeyPair,
            initialRecipes,
            initiallyEnabledReverseMapTypes,
            initiallyEnabledReverseMapTypesForIdObjects
        });
    }
    const recipes = (await Promise.all([...instance.obj.recipe].map(hash => getObjectWithType(hash, 'Recipe')))).filter(recipe => !hasRecipe(recipe.name));
    recipes.map(recipe => {
        return addRecipeToRuntime(recipe);
    });
    // Also add any initialRecipes not already registered (handles recipes added after instance creation)
    initialRecipes.filter(r => !hasRecipe(r.name)).forEach(r => addRecipeToRuntime(r));
    instance.obj.enabledReverseMapTypes.forEach((props, type) => addEnabledRvMapType(type, props));
    instance.obj.enabledReverseMapTypesForIdObjects.forEach((props, type) => addEnabledRvMapTypeForIdObjects(type, props));
    InstanceName = name;
    InstanceOwnerIdHash = instance.obj.owner;
    InstaceOwnerEmail = email;
    InstanceDirectory = directory;
    // Assign it to the module-level variable only now that all steps finished without error, so
    // that it remains undefined if this function exits early with error
    InstanceIdHash = instanceIdHash;
    return instance.obj;
}
/**
 * TODO REMOVE THIS FUNCTION - Just call instance-updater directly. This is a remnant from when
 * calling that module was cumbersome because it was a Plan module.
 *
 * Adds a recipe consisting of an array of objects of type RecipeRule for the given type
 * string. The recipe's rules are checked for the property names and the corresponding types but
 * no further: For example, if you forget "itemprop" (mandatory, a string) in a rule, or if you
 * have the wrong type of item on a rule property an Error is thrown. However, for "type" and
 * a rule's "itemprop" string property we only guard against HTML-breaking "<" and ">" and
 * against whitespace, and we don't check if any other recipes you refer to exist and a wide
 * range of conceivable problems.
 *
 * A recipe for a ONE object type consists of arrays of objects describing the data properties.
 * Allowed rule properties are explained in {@link RecipeRule}:
 *
 * ### Example
 *
 * The following code adds a recipe for a ONE object type "Mailbox":
 *
 * ```javascript
 * import * as ObjectRecipes from 'one.core/lib/object-recipes.js';
 *
 * if (!ObjectRecipes.hasRecipe('Mailbox')) {
 *     ObjectRecipes.addRecipeToRuntime({
 *         $type$: 'Recipe',
 *         name: 'Mailbox',
 *         rule: [
 *             // A SHA-256 hash pointing to an OneTest$ImapAccount object that this mailbox
 *             // belongs to. Both the account and the name are the ID attributes of this
 *             // VERSIONED object, meaning any Mailbox object with the same account and name
 *             // will be a version of the same object, varying only in the data properties not
 *             // marked as "isID:true".
 *             { itemprop: 'account', isId: true, referenceToObj: new Set(['Reference','Account'])
 * },
 *             { itemprop: 'name', isId: true },
 *             // This is an IMAP protocol feature to check if the IMAP-UIDs from last time are
 *             // still valid. This 32-bit integer can be fully represented by a Javascript number.
 *             { itemprop: 'uidValidity', valueType: 'number' },
 *             // A JSON-stringified UID => Email-BLOB-hash map
 *             { itemprop: 'uidEmailBlobMap', valueType: 'object' },
 *         ]
 *     });
 * }
 * ```
 *
 * A ONE object created using this recipe would look like this (indentation and newlines added
 * fore readability, not present in the actual microdata):
 *
 * ```html
 * <div itemscope itemtype="//refin.io/Mailbox">
 *   <span itemprop="account">8296cf598c1af767b5287....2bd96eae03448da3066aa</span>
 *   <span itemprop="name">INBOX</span>
 *   <span itemprop="uidValidity">1455785767</span>
 *   <span itemprop="uidEmailBlobMap">...[JSON]...</span>
 * </span>
 * ```
 * @static
 * @async
 * @param {Recipe[]} recipes - Array of {@link Recipe|ONE "Recipe" objects} to add to
 * the currently active instance **permanently**
 * @returns {ObjectCreation[]} Returns the result of creating the new Instance object
 * and the new Recipe objects
 */
export async function registerRecipes(recipes) {
    return await updateInstance({ recipes });
}
/**
 * If initInstance() is to be called more than once close() must be called first. There can be
 * only a single ONE instance in a runtime environment. This is used by mocha storage tests which do
 * the same storage-init followed by storage-deletion for each test file.
 * @static
 * @returns {undefined}
 */
export function closeInstance() {
    closeStorage();
    lockKeyChain();
    clearRuntimeRecipes();
    clearRvMapTypes();
    InstanceName = undefined;
    InstanceIdHash = undefined;
    InstanceDirectory = undefined;
    InstaceOwnerEmail = undefined;
}
/**
 * Closes & deletes the current instance.
 *
 * When the InstanceIdHash is undefined the instance was not initialized or is already closed.
 * @returns {Promise<void>}
 */
export async function closeAndDeleteCurrentInstance() {
    if (InstanceIdHash === undefined) {
        throw createError('IN-CADCI1');
    }
    const currentInstanceId = InstanceIdHash;
    closeInstance();
    await deleteStorage(currentInstanceId);
}
/**
 * Allows checking if an instance exists without an active instance.
 * @param {InstanceOptions.name} instanceName
 * @param {InstanceOptions.email} email
 * @returns {Promise<boolean>}
 */
export async function instanceExists(instanceName, email) {
    const instanceIdHash = await calculateInstanceIdHash(instanceName, email);
    return await doesStorageExist(instanceIdHash);
}
/**
 * Allows deleting an instance without an active instance.
 * This is important e.g. in case of a forgotten password.
 *
 * @param {InstanceOptions.name} instanceName
 * @param {InstanceOptions.email} email
 * @returns {Promise<boolean>}
 */
export async function deleteInstance(instanceName, email) {
    const instanceIdHash = await calculateInstanceIdHash(instanceName, email);
    if (!(await instanceExists(instanceName, email))) {
        // We want to throw an error if someone tries to delete an instance that
        // doesn't exist deleteStorage would silently ignore it.
        throw createError('IN-DI1', { instanceName, email });
    }
    await deleteStorage(instanceIdHash);
}
/**
 * Allows to calculate an instanceIdHash without an active instance needed to
 * 1. check if an inactive instance exists
 * 2. delete an inactive instance
 *
 * IMPORTANT: Needs to be adjusted by microdata changes
 *
 * @param {InstanceOptions.name} instanceName
 * @param {InstanceOptions.email} email
 * @returns {Promise<SHA256IdHash<Instance>>}
 */
export async function calculateInstanceIdHash(instanceName, email) {
    const ownerIdHash = (await createCryptoHash(`<div ${ID_OBJECT_ATTR} itemscope itemtype="//refin.io/Person"><span itemprop="email">${email}</span></div>`));
    return (await createCryptoHash(`<div ${ID_OBJECT_ATTR} itemscope itemtype="//refin.io/Instance"><span itemprop="name">${instanceName}</span><a itemprop="owner" data-type="id">${ownerIdHash}</a></div>`));
}
//# sourceMappingURL=instance.js.map